import { initConfig } from '@sunbit-dev/common/services/config';
import deviceId from '@sunbit-dev/common/services/deviceId';
import httpClient from '@sunbit-dev/common/services/httpClient';
import { ConnectedRouter } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import qs from 'qs';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import jwtDecode from 'jwt-decode';
import * as applicationLandingActions from './actions/applicationLandingActions';
import App from './app/App';
import './index.css';
import sunbitApi from './services/sunbitApi';
import * as serviceWorker from './serviceWorker';
import createStore from './store/createStore';
import { initDataDog } from '@sunbit-dev/common/services/datadog';
import mixpanel from 'mixpanel-browser';
import urls from './constants/urls';
import { fingerprintInit } from '@sunbit-dev/common/services/fingerprint';
import { samplingInit } from '@sunbit-dev/common/services/sampling';
import { visitId } from '@sunbit-dev/common/services/visitId';
import ISunbitToken from './models/ISunbitToken';
import { getURLForSampling } from './utils/helpers/userDataHelper';
import { sunbitFeatureStore } from './services/launchDarkly';
import { IFeatureStoreConfig } from './models/IFeatureStoreConfig';

(async () => {
  const history = createBrowserHistory();
  const configService = await initConfig(window.sunbitEnv || 'dev', '/config', window.sunbitE2eEnvNamespace);
  if (window.sunbitE2eEnvNamespace !== undefined) {
    configService.setItem('featureFlags', {
      isAddressAutocomplete: false,
    });
  }
  const featureStoreConfig: IFeatureStoreConfig = configService.getItem('launchDarkly');
  await sunbitFeatureStore.initFeatureStore(featureStoreConfig);

  if (window.sunbitEnv === 'oregon-prod') {
    mixpanel.init(configService.getItem('mixpanelToken'), { api_host: urls.mixPanelProxyOregon });
  } else {
    mixpanel.init(configService.getItem('mixpanelToken'), { api_host: urls.mixPanelProxy });
  }

  if (window.sunbitEnv !== 'automation' && window.sunbitEnv !== 'ci') {
    const dataDogConfig = {
      applicationId: 'a8a5b309-fed9-45f3-b7bb-de39fb653e61',
      clientToken: 'pub3f81f922dbfcbd8c492269375c950de4',
      site: 'datadoghq.com',
      env: window.sunbitEnv,
      service: 'card-apply',
      version: '1.0.0',
      sampleRate: 100,
      trackInteractions: true,
      allowedTracingUrls: [`${configService.getItem('apiUrlPci')}/card-service/api/v1`],
    };
    initDataDog(dataDogConfig);
  }
  let decodedToken: ISunbitToken;
  const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const applicationId = queryParams.applicationId || 0;
  const pendingApplicationId = queryParams.pendingApplicationId || 0;
  const token = queryParams.token || '';
  const urlForSampling: string = getURLForSampling(queryParams);

  // if the user enters the application trough the personalized token app
  if (token) {
    decodedToken = jwtDecode(token as string);
    fingerprintInit(configService.getItem('fingerprintjs')).then((fingerprint) => {
      const { visitorId, requestId } = fingerprint.getFingerprintResult();
      sampling.setCommonDataKey('fingerprintjs', { visitorId, requestId });
      sampling.setCommonDataKey('customerId', decodedToken.additionalProperties?.customerId);
      sampling.event('card-apply app started', {
        cardApplicationId: applicationId as number,
        url: urlForSampling,
      });
    });
  }
  const referringPartner = queryParams.referringPartner || '';
  const flow = queryParams.flow || null; // used in tests

  httpClient
    .setBaseUrl(configService.getItem('apiUrl'), configService.getItem('apiUrlPci'))
    .setHeader('deviceId', `ePay_${deviceId}`);

  if (flow !== null) {
    httpClient.setHeader('flow', flow as string);
  }

  const sampling = await samplingInit(configService.getItem('sampling'), {
    application: configService.getItem('application'),
    env: window.sunbitEnv,
    deviceId,
    visitId,
  });

  sunbitApi.init(httpClient);
  const store = createStore(history);
  const initAction = applicationLandingActions.initApplication({
    applicationId: applicationId as number,
    token: token as string,
    referringPartner: referringPartner as string,
    pendingApplicationId: pendingApplicationId as number,
  });
  if (initAction) {
    store.dispatch(initAction);
  }

  const AppComponent = (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <App />
      </ConnectedRouter>
    </Provider>
  );

  ReactDOM.render(AppComponent, document.getElementById('root'));
  // If you want your app to work offline and load faster, you can change
  // unregister() to register() below. Note this comes with some pitfalls.
  // Learn more about service workers: https://bit.ly/CRA-PWA
  serviceWorker.unregister();
})();
